import { AloftPrice, expected, schQueryParams } from '@aloft/toolkit-shared';

import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CraftsmanRecommendCompsModal } from '../components/RecommendedCompSelection/CraftsmanRecommendCompsModal.tsx';
import { AddComparablesModal } from '../modules/analysis/modals/addcomparables/AddComparablesModal.tsx';
import { useUpdateOrderFeatures } from '../modules/orderManagement/components/OrderTable/hooks/useUpdateOrderFeatures.ts';
import { isRecommendedCompID } from '../modules/referenceView/referenceViewID/isRecommendedCompID.ts';
import { addComps } from '../redux/features/actions/addComps.ts';
import { setMarketAdjustments } from '../redux/features/orderSlice.ts';
import { setProperties, setReferenceViewOpen } from '../redux/features/referenceBarUISlice.ts';
import {
  selectCompSequence,
  selectEffectiveDate,
  selectMarketAdjustments,
} from '../redux/features/selectors/orderSelectors.ts';
import { selectReferenceBarUI } from '../redux/features/selectors/referenceBarUISelectors.ts';
import { selectCompPropertyDataEntities } from '../redux/features/selectors/selectCompPropertyDataEntities.ts';
import { selectSubjectPropertyDataEntity } from '../redux/features/selectors/selectSubjectPropertyDataEntity.ts';
import { useAppDispatch, useAppSelector } from '../redux/hooks.ts';

export function useCraftsmanAddCompModals(): {
  addCompsModal: JSX.Element | false;
  addRecommendedCompsModal: JSX.Element | false;
  openAddCompsModal: () => void;
  openAddRecommendedCompsModal: () => void;
} {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams();
  const dispatch = useAppDispatch();
  const effectiveDate = useAppSelector(selectEffectiveDate);
  const subject = useAppSelector(selectSubjectPropertyDataEntity);
  const currentComps = useAppSelector(selectCompPropertyDataEntities);
  const { properties } = useAppSelector(selectReferenceBarUI);
  const marketAdjustments = useAppSelector(selectMarketAdjustments);
  const updateOrderFeatures = useUpdateOrderFeatures();

  const compSequence = useAppSelector(selectCompSequence);

  const [isAddCompsModalOpen, setIsAddCompsModalOpen] = useState<boolean>(false);
  const [isAddRecommendedCompsModalOpen, setIsAddRecommendedCompsModalOpen] =
    useState<boolean>(false);

  const openAddCompsModal = useCallback(() => {
    setIsAddCompsModalOpen(true);
  }, []);

  const closeManualAddPropertiesModal = useCallback(() => {
    setIsAddCompsModalOpen(false);
  }, []);

  const addCompsModal = isAddCompsModalOpen && (
    <AddComparablesModal
      isModalOpen={isAddCompsModalOpen}
      onModalClose={closeManualAddPropertiesModal}
    />
  );

  const openAddRecommendedCompsModal = useCallback(() => {
    dispatch(setReferenceViewOpen(false));
    setIsAddRecommendedCompsModalOpen(true);

    const params = new URLSearchParams(location.search);
    if (!params.has(schQueryParams.enum.addComps)) {
      params.set(schQueryParams.enum.addComps, 'true');
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [location.search, navigate, dispatch]);

  const closeAddRecommendedCompsModal = useCallback(() => {
    setIsAddRecommendedCompsModalOpen(false);
    // Clean up any tkl: recommended comps not added to the order
    dispatch(setProperties([...properties.filter((p) => !isRecommendedCompID(p.id))]));

    const params = new URLSearchParams(location.search);
    if (params.has(schQueryParams.enum.addComps)) {
      params.delete(schQueryParams.enum.addComps);
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [location.search, navigate, dispatch, properties]);

  useEffect(() => {
    if (!currentComps) return;

    const transactions = Object.values(currentComps)
      .map((property) => property.editableData.transaction)
      .flat();
    const closedTransactions = transactions
      .filter((t) => t.status === 'SettledSale')
      .filter((t) => t.price && t.originalListPrice);
    const activeTransactions = transactions.filter((t) =>
      ['Active', 'Contract'].includes(t.status ?? ''),
    );
    const listToSaleMethod = marketAdjustments.cachedForms.listToSale.selectedMethod;
    if (
      closedTransactions.length > 0 &&
      activeTransactions.length > 0 &&
      listToSaleMethod === 'none'
    ) {
      const averageClosedListToSale =
        closedTransactions.reduce((sum, t) => {
          const listPrice = AloftPrice.schema.parse(t.originalListPrice);
          const salePrice = AloftPrice.schema.parse(t.price);
          const salePriceToListRatio = (salePrice.toDollarLike() * 100) / listPrice.toDollarLike();
          return sum + salePriceToListRatio;
        }, 0) / closedTransactions.length;

      dispatch(
        setMarketAdjustments({
          ...marketAdjustments,
          cachedForms: {
            ...marketAdjustments.cachedForms,
            listToSale: {
              selectedMethod: 'customRatio',
              methodState: {
                customRatio: {
                  ratio: Math.round(averageClosedListToSale),
                },
              },
            },
          },
        }),
      );
    }
  }, [currentComps]);

  const addRecommendedCompsModal =
    isAddRecommendedCompsModalOpen && subject && effectiveDate ? (
      <CraftsmanRecommendCompsModal
        orderID={expected(orderId)}
        effectiveDate={effectiveDate}
        compSeq={compSequence}
        currentComps={currentComps}
        createComps={(comps) => {
          dispatch(addComps(comps));
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          if (orderId) updateOrderFeatures(orderId, comps);
        }}
        subject={subject}
        isModalOpen={isAddRecommendedCompsModalOpen}
        onModalClose={closeAddRecommendedCompsModal}
      />
    ) : (
      false
    );

  return {
    addCompsModal,
    addRecommendedCompsModal,
    openAddCompsModal,
    openAddRecommendedCompsModal,
  };
}
